import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useLockSavingsVault } from "@/features/savings/api/use-lock-savings-vault";
import type { SavingsVaultDto } from "@/features/savings/api/dto";
import {
  createLockSavingsVaultSchema,
  type LockSavingsVaultFormValues,
} from "@/features/savings/schemas/savings-vault.schema";

/**
 * Blocage / prolongation d'un coffre (`POST /SavingsVaults/{id}/lock`,
 * idempotent) — échéance UTC dans la fenêtre serveur 1–1 825 jours, jamais
 * raccourcie (le schéma le vérifie contre `lockedUntil` en cours).
 * Opération sans frais : `MutationConfirmPreview` avant l'appel API.
 */
export function VaultLockDialog({
  vault,
  open,
  onOpenChange,
}: {
  vault: SavingsVaultDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  const tSavings = (key: string) => t(key as TFunctionType);
  const schema = useMemo(
    () => createLockSavingsVaultSchema(vault.lockedUntil),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : schéma zod traduit reconstruit au changement de langue.
    [i18n.language, vault.lockedUntil]
  );

  const lock = useLockSavingsVault(vault.id);
  const pending = lock.isPending;
  const [preview, setPreview] = useState<LockSavingsVaultFormValues | null>(
    null
  );

  const form = useForm({
    defaultValues: { lockedUntil: "" } as LockSavingsVaultFormValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(parsed.data);
    },
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t("savings.vaults.lock.title")}</DialogTitle>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field
              name="lockedUntil"
              validators={{
                onChange: ({ value }) => {
                  const result = schema.shape.lockedUntil.safeParse(value);
                  return result.success
                    ? undefined
                    : result.error.issues[0]?.message;
                },
              }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="savings-lock-until">
                    {t("savings.vaults.lock.lockedUntil")}
                  </Label>
                  <Input
                    id="savings-lock-until"
                    type="datetime-local"
                    className="notranslate"
                    placeholder={t("savings.vaults.lock.lockedUntilPlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("savings.vaults.lock.hint")}
                  </p>
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreview(null);
        }}
        data={
          preview
            ? {
                vaultId: vault.id,
                vaultName: vault.name,
                currentLockedUntil: vault.lockedUntil
                  ? formatDateWithFallback(vault.lockedUntil, "—")
                  : null,
                newLockedUntil: new Date(preview.lockedUntil).toISOString(),
              }
            : {}
        }
        title={t("savings.vaults.lock.previewTitle")}
        description={t("savings.vaults.lock.previewDescription")}
        confirmLabel={t("savings.vaults.actions.lock")}
        t={tSavings}
        pending={pending}
        onConfirm={() => {
          if (!preview) return;
          lock.mutate(
            { lockedUntil: new Date(preview.lockedUntil).toISOString() },
            {
              onSuccess: () => {
                notifSilence(t("savings.vaults.feedback.locked"));
                setPreview(null);
                onOpenChange(false);
              },
              onError: (error) =>
                notifSilenceError(apiErrorHandler(error) || error.message),
            }
          );
        }}
      />
    </>
  );
}
